import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Alert,
  CircularProgress,
  Snackbar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  Skeleton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  AccountBalance, TrendingUp, AccountBalanceWallet, Sync, ShoppingCart, Close,
  Repeat, AssignmentReturn, ArrowForward, HelpOutline,
} from '@mui/icons-material';
import { plaidService, Transaction, NetWorthDataPoint, ManualAccount, RecurringStream, NetIncomeLast3MonthsResponse } from '../services/plaidService';
import { CreditReturnsAnalytics, CreditReturn } from '../services/analyticsService';
import {
  cachedSpending2,
  cachedIncome2,
  cachedNetWorthTrend,
  cachedRecurring,
  cachedCreditReturns,
  cachedNetIncomeLast3Months,
  cachedManualAccounts,
  invalidateAllData,
} from '../services/prefetch';
import SpendingBreakdown from './SpendingBreakdown';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';

const Dashboard: React.FC = () => {
  const [isLinked, setIsLinked] = useState<boolean | null>(null);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [showSyncMessage, setShowSyncMessage] = useState(false);
  const SPENDING_PAGE_SIZE = 25;
  const [spendingTransactions, setSpendingTransactions] = useState<Transaction[]>([]);
  const [spendingPage, setSpendingPage] = useState(1);
  const [spendingTotal, setSpendingTotal] = useState(0);
  const [spendingLoadingMore, setSpendingLoadingMore] = useState(false);
  const INCOME_PAGE_SIZE = 25;
  const [incomeTransactions, setIncomeTransactions] = useState<Transaction[]>([]);
  const [incomePage, setIncomePage] = useState(1);
  const [incomeTotal, setIncomeTotal] = useState(0);
  const [incomeLoadingMore, setIncomeLoadingMore] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [netWorthTrend, setNetWorthTrend] = useState<NetWorthDataPoint[]>([]);
  const [trendLoading, setTrendLoading] = useState(true);
  const [trendPeriod, setTrendPeriod] = useState<string>('3m');
  const [manualAccounts, setManualAccounts] = useState<ManualAccount[]>([]);
  const [recurringInflows, setRecurringInflows] = useState<RecurringStream[]>([]);
  const [recurringOutflows, setRecurringOutflows] = useState<RecurringStream[]>([]);
  const [recurringLoading, setRecurringLoading] = useState(true);
  const [creditReturns, setCreditReturns] = useState<CreditReturnsAnalytics | null>(null);
  const [creditReturnsLoading, setCreditReturnsLoading] = useState(true);
  const [selectedReturn, setSelectedReturn] = useState<CreditReturn | null>(null);
  const [netIncome, setNetIncome] = useState<NetIncomeLast3MonthsResponse | null>(null);
  const [netIncomeLoading, setNetIncomeLoading] = useState(true);
  const navigate = useNavigate();
  const didFetch = useRef(false);

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    checkLinkedAccounts();
  }, []);

  const checkLinkedAccounts = () => {
    // Kick off all fetches in parallel — none block the main dashboard render.
    fetchTransactions();
    fetchNetWorthTrend('3m');
    fetchManualAccounts();
    fetchRecurring();
    fetchCreditReturns();
    fetchNetIncome();
  };

  const fetchTransactions = async () => {
    setTransactionsLoading(true);
    try {
      const [spendingRes, incomeRes] = await Promise.all([
        cachedSpending2(1, SPENDING_PAGE_SIZE),
        cachedIncome2(1, INCOME_PAGE_SIZE),
      ]);
      const spending = spendingRes.transactions || [];
      const income = incomeRes.transactions || [];
      setSpendingTransactions(spending);
      setSpendingTotal(spendingRes.total ?? spending.length);
      setSpendingPage(1);
      setIncomeTransactions(income);
      setIncomeTotal(incomeRes.total ?? income.length);
      setIncomePage(1);
      setIsLinked(spending.length > 0 || income.length > 0);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setIsLinked(false);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const fetchNetWorthTrend = async (period: string) => {
    try {
      setTrendLoading(true);
      const trendRes = await cachedNetWorthTrend(period);
      setNetWorthTrend(trendRes.net_worth_trend || []);
    } catch (error) {
      console.error('Error fetching net worth trend:', error);
    } finally {
      setTrendLoading(false);
    }
  };

  const fetchRecurring = async () => {
    try {
      setRecurringLoading(true);
      const data = await cachedRecurring();
      setRecurringInflows(data.inflow_streams || []);
      setRecurringOutflows(data.outflow_streams || []);
    } catch (error) {
      console.error('Error fetching recurring transactions:', error);
    } finally {
      setRecurringLoading(false);
    }
  };

  const fetchCreditReturns = async () => {
    try {
      setCreditReturnsLoading(true);
      const data = await cachedCreditReturns();
      setCreditReturns(data);
    } catch (error) {
      console.error('Error fetching credit returns:', error);
    } finally {
      setCreditReturnsLoading(false);
    }
  };

  const fetchNetIncome = async () => {
    try {
      setNetIncomeLoading(true);
      const data = await cachedNetIncomeLast3Months();
      setNetIncome(data);
    } catch (error) {
      console.error('Error fetching net income:', error);
      setNetIncome(null);
    } finally {
      setNetIncomeLoading(false);
    }
  };

  const formatNetIncomeMonth = (ym: string): string => {
    const m = /^(\d{4})-(\d{2})/.exec(ym);
    if (!m) return ym;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
    return d.toLocaleString('en-US', { month: 'short', year: '2-digit' });
  };

  const handlePeriodChange = (period: string) => {
    setTrendPeriod(period);
    fetchNetWorthTrend(period);
  };

  const fetchManualAccounts = async () => {
    try {
      const accounts = await cachedManualAccounts();
      setManualAccounts(accounts);
    } catch (error) {
      console.error('Error fetching manual accounts:', error);
    }
  };

  const formatCurrency = (value: number) =>
    `${value < 0 ? '-' : ''}$${Math.abs(value).toLocaleString('en-US', {
      maximumFractionDigits: 0,
    })}`;


  const formatShortDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const formatDayHeader = (dateStr: string) => {
    const d = new Date(dateStr);
    const day = new Date(d); day.setHours(0, 0, 0, 0);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((today.getTime() - day.getTime()) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return d.toLocaleDateString('en-US', { weekday: 'long' });
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const groupByDay = <T extends { date: string }>(items: T[]): Array<[string, T[]]> => {
    const groups = new Map<string, T[]>();
    for (const item of items) {
      const key = formatDayHeader(item.date);
      const arr = groups.get(key);
      if (arr) arr.push(item); else groups.set(key, [item]);
    }
    return Array.from(groups.entries());
  };

  const formatFrequency = (f: string) =>
    f.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

  const loadMoreSpending = async () => {
    if (spendingLoadingMore) return;
    setSpendingLoadingMore(true);
    try {
      const nextPage = spendingPage + 1;
      const res = await plaidService.getSpending2(nextPage, SPENDING_PAGE_SIZE);
      const more = res.transactions || [];
      setSpendingTransactions((prev) => [...prev, ...more]);
      setSpendingTotal(res.total ?? (spendingTransactions.length + more.length));
      setSpendingPage(nextPage);
    } catch (error) {
      console.error('Error loading more spending:', error);
    } finally {
      setSpendingLoadingMore(false);
    }
  };

  const hasMoreSpending = spendingTransactions.length < spendingTotal;

  const loadMoreIncome = async () => {
    if (incomeLoadingMore) return;
    setIncomeLoadingMore(true);
    try {
      const nextPage = incomePage + 1;
      const res = await plaidService.getIncome2(nextPage, INCOME_PAGE_SIZE);
      const more = res.transactions || [];
      setIncomeTransactions((prev) => [...prev, ...more]);
      setIncomeTotal(res.total ?? (incomeTransactions.length + more.length));
      setIncomePage(nextPage);
    } catch (error) {
      console.error('Error loading more income:', error);
    } finally {
      setIncomeLoadingMore(false);
    }
  };

  const hasMoreIncome = incomeTransactions.length < incomeTotal;

  const handleSyncData = async () => {
    setSyncing(true);
    try {
      const result = await plaidService.syncData();
      setSyncMessage(`${result.message}. Synced ${result.accounts_synced} accounts and ${result.transactions_synced} transactions.`);
      setShowSyncMessage(true);

      // Sync changed underlying data → drop all cached responses, then refetch
      invalidateAllData();
      await checkLinkedAccounts();
    } catch (error) {
      console.error('Error syncing data:', error);
      setSyncMessage('Failed to sync data. Please try again.');
      setShowSyncMessage(true);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Container maxWidth="xl" className="animate-fade-in">
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 600,
            color: '#0a0a0a',
            letterSpacing: '-0.025em',
            mb: 0.5,
          }}
        >
          Dashboard
        </Typography>
        <Typography variant="body1" sx={{ color: '#5c5c5c' }}>
          Your financial overview at a glance
        </Typography>
      </Box>

      {isLinked === false && (
        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              fontSize: 28
            }
          }}
        >
          Welcome to Spenwell! To get started, please link your bank account using Plaid.
        </Alert>
      )}
      {isLinked === true && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              fontSize: 28
            }
          }}
        >
          Your account is successfully linked! View your financial data below.
        </Alert>
      )}

      {/* Net Worth Breakdown */}
      {(() => {
        // Use the latest data point from the trend API as the single source of truth
        const latestTrend = netWorthTrend.length > 0 ? netWorthTrend[netWorthTrend.length - 1] : null;
        const netWorth = latestTrend ? latestTrend.net_worth : 0;
        const assets = latestTrend ? latestTrend.assets : 0;
        const liabilities = latestTrend ? latestTrend.liabilities : 0;
        const realEstate = manualAccounts
          .filter(a => a.type === 'real_estate')
          .reduce((sum, a) => sum + (Number(a.current_balance) || 0), 0);
        const trendDate = latestTrend
          ? new Date(latestTrend.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const tiles = [
          { label: 'Assets',              value: assets,       icon: '💰', iconBg: '#d1fae5', iconColor: '#059669' },
          { label: 'Real estate',         value: realEstate,   icon: '🏠', iconBg: '#ede9fe', iconColor: '#7c3aed' },
          { label: 'Liabilities',         value: -liabilities, icon: '💳', iconBg: '#fee2e2', iconColor: '#dc2626' },
        ];
        return (
          <Card sx={{ mb: 4, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Net worth
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}>
                    {formatCurrency(netWorth)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary" display="block">as of</Typography>
                  <Typography variant="caption" color="text.secondary">{trendDate}</Typography>
                </Box>
              </Box>

              {/* Tile grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${tiles.length}, 1fr)`, gap: 1.5 }}>
                {tiles.map(tile => {
                  const isNegative = tile.value < 0;
                  return (
                    <Box
                      key={tile.label}
                      sx={{
                        background: '#f8fafc',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 3,
                        p: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2,
                          background: tile.iconBg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 18,
                          mb: 1.5,
                        }}
                      >
                        {tile.icon}
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        display="block"
                        sx={{ mb: 0.5, fontSize: '0.8125rem', fontWeight: 500 }}
                      >
                        {tile.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: isNegative ? '#c43e3e' : '#0a0a0a',
                          fontVariantNumeric: 'tabular-nums',
                          fontSize: '1.375rem',
                          letterSpacing: '-0.015em',
                          lineHeight: 1.2,
                        }}
                      >
                        {formatCurrency(tile.value)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        );
      })()}

      {/* Net Worth Trend Chart */}
      {isLinked !== false && (() => {
        const raw = netWorthTrend;
        const hasData = raw.length > 0;
        const periodStartValue = hasData ? raw[0].net_worth : 0;
        const latestValue = hasData ? raw[raw.length - 1].net_worth : 0;
        const delta = latestValue - periodStartValue;
        const pct = periodStartValue !== 0 ? (delta / Math.abs(periodStartValue)) * 100 : 0;
        const isUp = delta >= 0;
        const lineColor = latestValue >= 0 ? '#0f7a5a' : '#c43e3e';
        const deltaColor = isUp ? '#0f7a5a' : '#c43e3e';
        const deltaBg = isUp ? '#e8f3ee' : '#fbeae9';
        const periodLabels: Record<string, string> = { '3m': '3 months', '6m': '6 months', '1y': '1 year', '5y': '5 years' };

        return (
          <Card sx={{ mb: 4, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#5c5c5c', mb: 0.5 }}>
                    Net Worth Trend
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '1.625rem', letterSpacing: '-0.02em', color: '#0a0a0a', fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(latestValue)}
                    </Typography>
                    {hasData && (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          bgcolor: deltaBg,
                          color: deltaColor,
                          fontWeight: 600,
                          fontSize: '0.8125rem',
                          px: 1,
                          py: 0.375,
                          borderRadius: 1.25,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        <Box component="span" sx={{ fontSize: '0.75rem', lineHeight: 1 }}>
                          {isUp ? '▲' : '▼'}
                        </Box>
                        {isUp ? '+' : '−'}{formatCurrency(Math.abs(delta))}
                        <Box component="span" sx={{ color: deltaColor, opacity: 0.75 }}>
                          ({isUp ? '+' : '−'}{Math.abs(pct).toFixed(1)}%)
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: '#8a8a8a', display: 'block', mt: 0.5 }}>
                    Past {periodLabels[trendPeriod] || '3 months'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0, background: '#f5f5f5', borderRadius: 1.5, p: 0.375, border: '1px solid #eaeaea' }}>
                  {[
                    { key: '3m', label: '3M' },
                    { key: '6m', label: '6M' },
                    { key: '1y', label: '1Y' },
                    { key: '5y', label: '5Y' },
                  ].map(opt => (
                    <Button
                      key={opt.key}
                      size="small"
                      onClick={() => handlePeriodChange(opt.key)}
                      disableRipple
                      sx={{
                        minWidth: 40,
                        px: 1.25,
                        py: 0.5,
                        fontSize: 12,
                        fontWeight: trendPeriod === opt.key ? 600 : 500,
                        borderRadius: 1,
                        color: trendPeriod === opt.key ? '#0a0a0a' : '#5c5c5c',
                        background: trendPeriod === opt.key ? '#ffffff' : 'transparent',
                        boxShadow: trendPeriod === opt.key ? '0 1px 2px rgba(15,15,15,0.06)' : 'none',
                        '&:hover': {
                          background: trendPeriod === opt.key ? '#ffffff' : 'rgba(10,10,10,0.04)',
                        },
                        transition: 'background-color 0.15s ease, color 0.15s ease',
                      }}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </Box>
              </Box>
              {trendLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                  <CircularProgress />
                </Box>
              ) : hasData ? (
                (() => {
                  const periodMonths: Record<string, number> = { '3m': 3, '6m': 6, '1y': 12, '5y': 60 };
                  const months = periodMonths[trendPeriod] || 3;
                  const dateFmt = (d: Date) =>
                    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

                  const now = new Date();
                  const periodStart = new Date(now);
                  periodStart.setMonth(periodStart.getMonth() - months);

                  const xMin = periodStart.getTime();
                  const xMax = now.getTime();

                  const rawWithTimestamps = raw.map(d => ({
                    ...d,
                    timestamp: new Date(d.date).getTime(),
                    dateLabel: dateFmt(new Date(d.date)),
                  }));
                  const firstPoint = rawWithTimestamps[0];
                  const lastPoint = rawWithTimestamps[rawWithTimestamps.length - 1];
                  const chartData = [
                    ...(firstPoint && firstPoint.timestamp > xMin
                      ? [{ ...firstPoint, timestamp: xMin, dateLabel: dateFmt(periodStart) }]
                      : []),
                    ...rawWithTimestamps,
                    ...(lastPoint && lastPoint.timestamp < xMax
                      ? [{ ...lastPoint, timestamp: xMax, dateLabel: dateFmt(now) }]
                      : []),
                  ];

                  const allValues = raw.map(d => d.net_worth);
                  const minVal = Math.min(...allValues, 0);
                  const maxVal = Math.max(...allValues, 0);
                  const padding = Math.max(Math.abs(maxVal - minVal) * 0.15, 1000);
                  const yDomain: [number, number] = [minVal - padding, maxVal + padding];

                  const tickCount = 5;
                  const xTicks: number[] = [];
                  for (let i = 0; i < tickCount; i++) {
                    xTicks.push(xMin + (xMax - xMin) * (i / (tickCount - 1)));
                  }

                  const gradientId = `nw-gradient-${trendPeriod}`;

                  return (
                    <Box sx={{ width: '100%', height: 320 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={chartData}
                          margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={lineColor} stopOpacity={0.18} />
                              <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid stroke="#f0f0f0" strokeDasharray="0" vertical={false} />
                          <XAxis
                            dataKey="timestamp"
                            type="number"
                            domain={[xMin, xMax]}
                            ticks={xTicks}
                            tickFormatter={(ts: number) => dateFmt(new Date(ts))}
                            tick={{ fontSize: 11, fill: '#8a8a8a', fontWeight: 500 }}
                            tickLine={false}
                            axisLine={false}
                            tickMargin={10}
                          />
                          <YAxis
                            domain={yDomain}
                            tick={{ fontSize: 11, fill: '#8a8a8a', fontWeight: 500 }}
                            tickLine={false}
                            axisLine={false}
                            width={56}
                            tickFormatter={(val: number) =>
                              `${val < 0 ? '-' : ''}$${Math.abs(val) >= 1000 ? `${(Math.abs(val) / 1000).toFixed(0)}k` : Math.abs(val).toFixed(0)}`
                            }
                          />
                          <ReferenceLine
                            y={0}
                            stroke="#c4c4c4"
                            strokeDasharray="3 3"
                            strokeWidth={1}
                          />
                          <Tooltip
                            cursor={{ stroke: '#1a1a1a', strokeWidth: 1, strokeDasharray: '4 4' }}
                            contentStyle={{
                              background: '#ffffff',
                              border: '1px solid #eaeaea',
                              borderRadius: 8,
                              boxShadow: '0 4px 12px rgba(15,15,15,0.06)',
                              fontSize: 12,
                              padding: '8px 12px',
                            }}
                            labelStyle={{ color: '#8a8a8a', fontSize: 11, fontWeight: 500, marginBottom: 4 }}
                            itemStyle={{ color: '#0a0a0a', fontWeight: 600, fontVariantNumeric: 'tabular-nums', padding: 0 }}
                            formatter={(value: number) => [
                              `${value < 0 ? '-' : ''}$${Math.abs(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
                              'Net Worth',
                            ]}
                            labelFormatter={(ts: number) => dateFmt(new Date(ts))}
                          />
                          <Area
                            type="monotone"
                            dataKey="net_worth"
                            name="Net Worth"
                            stroke={lineColor}
                            strokeWidth={2}
                            fill={`url(#${gradientId})`}
                            dot={false}
                            activeDot={{ r: 5, strokeWidth: 2, stroke: '#ffffff', fill: lineColor }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  );
                })()
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                  <TrendingUp sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">No trend data available yet.</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        );
      })()}



      {/* Spending Breakdown */}
      {isLinked !== false && <SpendingBreakdown embedded />}

      {/* Recurring Transactions & Returns/Refunds */}
      {isLinked !== false && (
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap', alignItems: 'stretch' }}>
      {(() => {
        const HIDDEN_CATEGORIES = ['LOAN_PAYMENTS_CREDIT_CARD_PAYMENT'];
        const isVisible = (s: RecurringStream) => s.is_active && !HIDDEN_CATEGORIES.includes(s.personal_finance_category);
        const visibleInflows = recurringInflows.filter(isVisible);
        const visibleOutflows = recurringOutflows.filter(isVisible);
        const visibleTotal = visibleInflows.length + visibleOutflows.length;
        return (
        <Card sx={{ flex: '1 1 0', minWidth: 0, height: 640, border: '1px solid', borderColor: 'divider', boxShadow: 'none', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 }, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Repeat sx={{ color: '#6366f1' }} />
              <Typography variant="h6" fontWeight={700}>Recurring Transactions</Typography>
              <Chip
                label={`${visibleTotal}`}
                size="small"
                sx={{ bgcolor: '#eef2ff', color: '#6366f1', fontWeight: 600 }}
              />
            </Box>
            {recurringLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : visibleTotal > 0 ? (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', mx: -1 }}>
                {[...visibleInflows.map(s => ({ s, isInflow: true })), ...visibleOutflows.map(s => ({ s, isInflow: false }))].map(({ s, isInflow }, idx, arr) => {
                  const label = s.merchant_name || s.description;
                  const initial = (label || '?').charAt(0).toUpperCase();
                  return (
                    <Box
                      key={s.stream_id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        px: 1,
                        py: 1.5,
                        borderBottom: idx < arr.length - 1 ? '1px solid #f0f0f0' : 'none',
                        transition: 'background-color 0.12s ease',
                        '&:hover': { bgcolor: '#fafafa' },
                      }}
                    >
                      <Avatar
                        variant="rounded"
                        sx={{ width: 36, height: 36, bgcolor: '#f5f5f5', color: '#1a1a1a', fontWeight: 600, fontSize: '0.9rem' }}
                      >
                        {initial}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap sx={{ color: '#0a0a0a' }}>
                          {label}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                          <Box
                            component="span"
                            sx={{
                              fontSize: 11,
                              fontWeight: 500,
                              color: '#5c5c5c',
                              bgcolor: '#f0f0f0',
                              borderRadius: 1,
                              px: 0.75,
                              py: 0.125,
                              letterSpacing: '-0.005em',
                            }}
                          >
                            {formatFrequency(s.frequency)}
                          </Box>
                          <Typography variant="caption" sx={{ color: '#8a8a8a' }}>
                            {isInflow ? 'income' : 'expense'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: '#0a0a0a', fontVariantNumeric: 'tabular-nums' }}
                        >
                          {isInflow ? '+' : '−'}{formatCurrency(Math.abs(s.last_amount.amount))}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#8a8a8a', fontVariantNumeric: 'tabular-nums' }}>
                          avg {formatCurrency(Math.abs(s.average_amount.amount))}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                <Repeat sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">No recurring transactions found.</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
        );
      })()}

      {/* Returns & Refunds */}
      <Card sx={{ flex: '1 1 0', minWidth: 0, height: 640, border: '1px solid', borderColor: 'divider', boxShadow: 'none', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 }, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AssignmentReturn sx={{ color: '#10b981' }} />
                <Typography variant="h6" fontWeight={700}>Returns & Refunds</Typography>
                {creditReturns && (
                  <Chip
                    label={`${creditReturns.count}`}
                    size="small"
                    sx={{ bgcolor: '#d1fae5', color: '#047857', fontWeight: 600 }}
                  />
                )}
              </Box>
              {creditReturns && creditReturns.count > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Total returned
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ color: '#047857', fontVariantNumeric: 'tabular-nums' }}
                    >
                      +{formatCurrency(creditReturns.total_returned)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {creditReturns && creditReturns.count > 0 && (
              <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    flex: '1 1 0',
                    minWidth: 140,
                    bgcolor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: 2,
                    px: 2,
                    py: 1.25,
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 10 }}>
                    Matched
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#047857', mt: 0.25 }}>
                    {creditReturns.matched_count}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: '1 1 0',
                    minWidth: 140,
                    bgcolor: '#fffbeb',
                    border: '1px solid #fde68a',
                    borderRadius: 2,
                    px: 2,
                    py: 1.25,
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 10 }}>
                    Unmatched
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#92400e', mt: 0.25 }}>
                    {creditReturns.unmatched_count}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: '1 1 0',
                    minWidth: 140,
                    bgcolor: '#f8fafc',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    px: 2,
                    py: 1.25,
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 10 }}>
                    Lookback
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#0f172a', mt: 0.25 }}>
                    {creditReturns.negative_days}d back · {creditReturns.positive_days}d match
                  </Typography>
                </Box>
              </Box>
            )}

            {creditReturnsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : creditReturns && creditReturns.returns.length > 0 ? (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', mx: -1 }}>
                {creditReturns.returns.map((ret, idx, arr) => {
                  const refund = ret.refund;
                  const original = ret.original_purchase;
                  const merchantLabel = refund.merchant_name || refund.name;
                  const initial = (merchantLabel || '?').charAt(0).toUpperCase();
                  const logoUrl = refund.logo_url || original?.logo_url || null;
                  return (
                    <Box
                      key={refund.transaction_id}
                      onClick={() => setSelectedReturn(ret)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        px: 1,
                        py: 1.5,
                        cursor: 'pointer',
                        borderBottom: idx < arr.length - 1 ? '1px solid #f0f0f0' : 'none',
                        transition: 'background-color 0.12s ease',
                        '&:hover': { bgcolor: '#fafafa' },
                      }}
                    >
                      <Avatar
                        variant="rounded"
                        src={logoUrl || undefined}
                        sx={{ width: 36, height: 36, bgcolor: '#f5f5f5', color: '#1a1a1a', fontWeight: 600, fontSize: '0.9rem' }}
                      >
                        {initial}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap sx={{ color: '#0a0a0a' }}>
                          {merchantLabel}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
                          {ret.matched && original ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Typography variant="caption" sx={{ color: '#8a8a8a', fontVariantNumeric: 'tabular-nums' }}>
                                {formatShortDate(original.date)}
                              </Typography>
                              <ArrowForward sx={{ fontSize: 12, color: '#cbd5e1' }} />
                              <Typography variant="caption" sx={{ color: '#5c5c5c', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                                {formatShortDate(refund.date)}
                              </Typography>
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 500,
                                  color: '#15803d',
                                  bgcolor: '#d1fae5',
                                  borderRadius: 1,
                                  px: 0.75,
                                  py: 0.125,
                                }}
                              >
                                {original.days_between}d
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Typography variant="caption" sx={{ color: '#5c5c5c', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                                {formatShortDate(refund.date)}
                              </Typography>
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.25,
                                  fontSize: 11,
                                  fontWeight: 500,
                                  color: '#b45309',
                                  bgcolor: '#fef3c7',
                                  borderRadius: 1,
                                  px: 0.75,
                                  py: 0.125,
                                }}
                              >
                                <HelpOutline sx={{ fontSize: 11 }} />
                                Unmatched
                              </Box>
                            </Box>
                          )}
                          <Typography variant="caption" sx={{ color: '#8a8a8a' }}>
                            {refund.account_name} ····{refund.account_mask}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: '#047857', fontVariantNumeric: 'tabular-nums' }}
                        >
                          +{formatCurrency(refund.amount)}
                        </Typography>
                        {ret.matched && original && (
                          <Typography variant="caption" sx={{ color: '#8a8a8a', fontVariantNumeric: 'tabular-nums' }}>
                            paid {formatCurrency(original.amount)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                <AssignmentReturn sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">No returns or refunds found.</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
      )}

      {/* Net income · last 3 months */}
      {isLinked !== false && (
        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#0a0a0a', letterSpacing: '-0.01em' }}>
                  Net income · last 3 months
                </Typography>
                <Typography variant="caption" sx={{ color: '#8a8a8a' }}>
                  Income minus spending
                </Typography>
              </Box>
              {netIncome?.start_date && netIncome?.end_date && (
                <Typography variant="caption" sx={{ color: '#8a8a8a', fontVariantNumeric: 'tabular-nums' }}>
                  {netIncome.start_date} → {netIncome.end_date}
                </Typography>
              )}
            </Box>

            {netIncomeLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 120 }}>
                <CircularProgress size={22} />
              </Box>
            ) : !netIncome ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 80 }}>
                <Typography variant="body2" sx={{ color: '#8a8a8a' }}>
                  No net income data available
                </Typography>
              </Box>
            ) : (
              <>
                {/* Top summary tiles */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 3, sm: 5 }, mb: 2.5 }}>
                  {[
                    { label: 'Total income', value: netIncome.total_income, color: '#0f7a5a' },
                    { label: 'Total spending', value: netIncome.total_spending, color: '#c43e3e' },
                    {
                      label: 'Net income',
                      value: netIncome.net_income,
                      color: netIncome.net_income >= 0 ? '#0f7a5a' : '#c43e3e',
                    },
                    {
                      label: 'Avg / month',
                      value: netIncome.monthly_average_net_income,
                      color: netIncome.monthly_average_net_income >= 0 ? '#0f7a5a' : '#c43e3e',
                    },
                  ].map((tile) => (
                    <Box key={tile.label} sx={{ flex: '1 1 160px', minWidth: 140 }}>
                      <Typography variant="body2" sx={{ color: '#8a8a8a', fontWeight: 500, mb: 0.5 }}>
                        {tile.label}
                      </Typography>
                      <Typography sx={{ fontWeight: 600, fontSize: '1.5rem', letterSpacing: '-0.02em', color: tile.color, fontVariantNumeric: 'tabular-nums' }}>
                        {tile.value < 0 ? '−' : ''}${Math.abs(tile.value).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Monthly chart — Net only (positive green, negative red) */}
                <Box sx={{ height: 240, mt: 1 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={netIncome.months.map((m) => ({
                        month: formatNetIncomeMonth(m.month),
                        Net: m.net_income,
                      }))}
                      margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 12, fill: '#5c5c5c' }}
                        tickLine={false}
                        axisLine={{ stroke: '#eaeaea' }}
                      />
                      <YAxis
                        tick={{ fontSize: 12, fill: '#5c5c5c' }}
                        tickLine={false}
                        axisLine={{ stroke: '#eaeaea' }}
                        tickFormatter={(value: number) => {
                          const sign = value < 0 ? '-' : '';
                          const abs = Math.abs(value);
                          return abs >= 1000 ? `${sign}$${(abs / 1000).toFixed(0)}k` : `${sign}$${abs}`;
                        }}
                      />
                      <ReferenceLine y={0} stroke="#c4c4c4" />
                      <Tooltip
                        cursor={{ fill: 'rgba(15,122,90,0.06)' }}
                        formatter={(value: any) => [
                          `${Number(value) < 0 ? '−' : ''}$${Math.abs(Number(value)).toLocaleString('en-US', { maximumFractionDigits: 2 })}`,
                          'Net',
                        ]}
                      />
                      <Bar dataKey="Net" radius={[4, 4, 0, 0]}>
                        {netIncome.months.map((m, i) => (
                          <Cell key={i} fill={m.net_income >= 0 ? '#0f7a5a' : '#c43e3e'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Monthly rows */}
                <Box sx={{ mt: 1 }}>
                  {netIncome.months.map((m, idx) => (
                    <Box
                      key={m.month}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        py: 1.25,
                        borderTop: idx === 0 ? '1px solid #f0f0f0' : 'none',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#0a0a0a', minWidth: 90 }}>
                        {formatNetIncomeMonth(m.month)}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 }, flexWrap: 'wrap', justifyContent: 'flex-end', flex: 1 }}>
                        <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                          <Typography variant="caption" sx={{ color: '#8a8a8a', display: 'block' }}>Income</Typography>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#0f7a5a', fontVariantNumeric: 'tabular-nums' }}>
                            ${m.income.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                          <Typography variant="caption" sx={{ color: '#8a8a8a', display: 'block' }}>Spending</Typography>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#c43e3e', fontVariantNumeric: 'tabular-nums' }}>
                            ${m.spending.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                          <Typography variant="caption" sx={{ color: '#8a8a8a', display: 'block' }}>Net</Typography>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: m.net_income >= 0 ? '#0f7a5a' : '#c43e3e', fontVariantNumeric: 'tabular-nums' }}>
                            {m.net_income < 0 ? '−' : ''}${Math.abs(m.net_income).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Spending & Income Cards */}
      {isLinked !== false && (
        <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
          {/* Spending */}
          <Card sx={{ flex: '1 1 0', minWidth: 0, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <ShoppingCart sx={{ color: '#ef4444' }} />
                <Typography variant="h6" fontWeight={600}>Spending Transactions</Typography>
                <Chip label={`${spendingTotal || spendingTransactions.length}`} size="small" color="error" variant="outlined" />
              </Box>
              {transactionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : spendingTransactions.length > 0 ? (
                <>
                  <Box sx={{ maxHeight: 480, overflow: 'auto', mx: -1 }}>
                    {groupByDay(spendingTransactions).map(([day, items]) => (
                      <Box key={day}>
                        <Box sx={{ px: 1, py: 0.75, position: 'sticky', top: 0, bgcolor: '#ffffff', zIndex: 1, borderBottom: '1px solid #f0f0f0' }}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, color: '#8a8a8a', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 10 }}
                          >
                            {day}
                          </Typography>
                        </Box>
                        {items.map((t, idx) => (
                          <Box
                            key={t.transaction_id}
                            onClick={() => setSelectedTransaction(t)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              px: 1,
                              py: 1.5,
                              cursor: 'pointer',
                              borderBottom: idx < items.length - 1 ? '1px solid #f0f0f0' : 'none',
                              transition: 'background-color 0.12s ease',
                              '&:hover': { bgcolor: '#fafafa' },
                            }}
                          >
                            {t.logo_url ? (
                              <Avatar src={t.logo_url} alt={t.merchant_name || ''} imgProps={{ loading: 'lazy', decoding: 'async' }} sx={{ width: 36, height: 36 }} variant="rounded" />
                            ) : (
                              <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: '#f5f5f5', color: '#1a1a1a', fontWeight: 600, fontSize: '0.9rem' }}>
                                {(t.merchant_name || t.name || '?').charAt(0).toUpperCase()}
                              </Avatar>
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={500} noWrap sx={{ color: '#0a0a0a' }}>
                                {t.merchant_name || t.name}
                              </Typography>
                              <Typography variant="caption" noWrap sx={{ color: '#8a8a8a', display: 'block' }}>
                                {t.personal_finance_category?.primary?.replace(/_/g, ' ').toLowerCase() || t.name}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ color: '#0a0a0a', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                            >
                              −{formatCurrency(Math.abs(t.amount))}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ))}
                    {spendingLoadingMore && (
                      <Box>
                        {[0, 1, 2].map((i) => (
                          <Box key={`skeleton-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                            <Skeleton variant="rounded" width={36} height={36} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Skeleton variant="text" width="55%" height={18} />
                              <Skeleton variant="text" width="35%" height={14} />
                            </Box>
                            <Skeleton variant="text" width={64} height={18} />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  {hasMoreSpending && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                      <Button
                        onClick={loadMoreSpending}
                        disabled={spendingLoadingMore}
                        variant="text"
                        size="small"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 500,
                          color: '#1a1a1a',
                          px: 2,
                          py: 0.75,
                          borderRadius: 999,
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': { bgcolor: '#fafafa', borderColor: '#d4d4d4' },
                        }}
                      >
                        {spendingLoadingMore
                          ? 'Loading…'
                          : `Show older transactions · ${spendingTotal - spendingTransactions.length} remaining`}
                      </Button>
                    </Box>
                  )}
                  {!hasMoreSpending && spendingTransactions.length > SPENDING_PAGE_SIZE && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                      <Typography variant="caption" sx={{ color: '#8a8a8a' }}>
                        You've reached the end · {spendingTotal} transactions
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                  <ShoppingCart sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">No spending transactions found.</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Income */}
          <Card sx={{ flex: '1 1 0', minWidth: 0, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <AccountBalanceWallet sx={{ color: '#10b981' }} />
                <Typography variant="h6" fontWeight={600}>Income Transactions</Typography>
                <Chip label={`${incomeTotal || incomeTransactions.length}`} size="small" color="success" variant="outlined" />
              </Box>
              {transactionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : incomeTransactions.length > 0 ? (
                <>
                  <Box sx={{ maxHeight: 480, overflow: 'auto', mx: -1 }}>
                    {groupByDay(incomeTransactions).map(([day, items]) => (
                      <Box key={day}>
                        <Box sx={{ px: 1, py: 0.75, position: 'sticky', top: 0, bgcolor: '#ffffff', zIndex: 1, borderBottom: '1px solid #f0f0f0' }}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, color: '#8a8a8a', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 10 }}
                          >
                            {day}
                          </Typography>
                        </Box>
                        {items.map((t, idx) => (
                          <Box
                            key={t.transaction_id}
                            onClick={() => setSelectedTransaction(t)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              px: 1,
                              py: 1.5,
                              cursor: 'pointer',
                              borderBottom: idx < items.length - 1 ? '1px solid #f0f0f0' : 'none',
                              transition: 'background-color 0.12s ease',
                              '&:hover': { bgcolor: '#fafafa' },
                            }}
                          >
                            {t.logo_url ? (
                              <Avatar src={t.logo_url} alt={t.merchant_name || ''} imgProps={{ loading: 'lazy', decoding: 'async' }} sx={{ width: 36, height: 36 }} variant="rounded" />
                            ) : (
                              <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: '#f5f5f5', color: '#1a1a1a', fontWeight: 600, fontSize: '0.9rem' }}>
                                {(t.merchant_name || t.name || '?').charAt(0).toUpperCase()}
                              </Avatar>
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={500} noWrap sx={{ color: '#0a0a0a' }}>
                                {t.merchant_name || t.name}
                              </Typography>
                              <Typography variant="caption" noWrap sx={{ color: '#8a8a8a', display: 'block' }}>
                                {t.personal_finance_category?.primary?.replace(/_/g, ' ').toLowerCase() || t.name}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ color: '#0f7a5a', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                            >
                              +{formatCurrency(Math.abs(t.amount))}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ))}
                    {incomeLoadingMore && (
                      <Box>
                        {[0, 1, 2].map((i) => (
                          <Box key={`income-skeleton-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                            <Skeleton variant="rounded" width={36} height={36} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Skeleton variant="text" width="55%" height={18} />
                              <Skeleton variant="text" width="35%" height={14} />
                            </Box>
                            <Skeleton variant="text" width={64} height={18} />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  {hasMoreIncome && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                      <Button
                        onClick={loadMoreIncome}
                        disabled={incomeLoadingMore}
                        variant="text"
                        size="small"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 500,
                          color: '#1a1a1a',
                          px: 2,
                          py: 0.75,
                          borderRadius: 999,
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': { bgcolor: '#fafafa', borderColor: '#d4d4d4' },
                        }}
                      >
                        {incomeLoadingMore
                          ? 'Loading…'
                          : `Show older income · ${incomeTotal - incomeTransactions.length} remaining`}
                      </Button>
                    </Box>
                  )}
                  {!hasMoreIncome && incomeTransactions.length > INCOME_PAGE_SIZE && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                      <Typography variant="caption" sx={{ color: '#8a8a8a' }}>
                        You've reached the end · {incomeTotal} transactions
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                  <AccountBalanceWallet sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">No income transactions found.</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Return Detail Dialog */}
      <Dialog
        open={!!selectedReturn}
        onClose={() => setSelectedReturn(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        {selectedReturn && (() => {
          const refund = selectedReturn.refund;
          const original = selectedReturn.original_purchase;
          const accentColor = '#10b981';
          const refundLogo = refund.logo_url || original?.logo_url || null;
          const merchantLabel = refund.merchant_name || refund.name;
          return (
            <>
              <DialogTitle
                sx={{
                  background: `linear-gradient(135deg, ${accentColor}15 0%, ${accentColor}08 100%)`,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 2,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                    {refundLogo ? (
                      <Avatar src={refundLogo} alt={merchantLabel} imgProps={{ loading: 'lazy', decoding: 'async' }} sx={{ width: 48, height: 48 }} variant="rounded" />
                    ) : (
                      <Avatar
                        sx={{ width: 48, height: 48, bgcolor: accentColor, fontSize: '1.1rem', fontWeight: 700 }}
                        variant="rounded"
                      >
                        {(merchantLabel || '?').charAt(0).toUpperCase()}
                      </Avatar>
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" fontWeight={700} noWrap>
                        {merchantLabel}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {selectedReturn.matched ? 'Refund matched to original purchase' : 'Refund · no matching purchase found'}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton onClick={() => setSelectedReturn(null)} size="small">
                    <Close />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ pt: 3 }}>
                {/* Refund summary */}
                <Box sx={{ textAlign: 'center', py: 2, mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                    Amount Refunded
                  </Typography>
                  <Typography
                    variant="h3"
                    fontWeight={800}
                    sx={{ color: accentColor, fontVariantNumeric: 'tabular-nums' }}
                  >
                    +{formatCurrency(refund.amount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {formatShortDate(refund.date)} · {refund.account_name} ····{refund.account_mask}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Original payment */}
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, display: 'block', mb: 1.5 }}>
                  Original Payment
                </Typography>

                {original ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: '#fafafa',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {original.logo_url ? (
                        <Avatar src={original.logo_url} alt={original.merchant_name || original.name} imgProps={{ loading: 'lazy', decoding: 'async' }} sx={{ width: 40, height: 40 }} variant="rounded" />
                      ) : (
                        <Avatar
                          sx={{ width: 40, height: 40, bgcolor: '#ef4444', fontSize: '1rem', fontWeight: 700 }}
                          variant="rounded"
                        >
                          {(original.merchant_name || original.name || '?').charAt(0).toUpperCase()}
                        </Avatar>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" fontWeight={600} noWrap>
                          {original.merchant_name || original.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatShortDate(original.date)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{ color: '#ef4444', fontVariantNumeric: 'tabular-nums' }}
                      >
                        −{formatCurrency(original.amount)}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                          Days to Refund
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                          {original.days_between} {original.days_between === 1 ? 'day' : 'days'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                          Net
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          sx={{
                            mt: 0.5,
                            color: refund.amount - original.amount === 0 ? '#0a0a0a' : refund.amount - original.amount > 0 ? '#047857' : '#ef4444',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {refund.amount - original.amount >= 0 ? '+' : '−'}
                          {formatCurrency(Math.abs(refund.amount - original.amount))}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      border: '1px dashed',
                      borderColor: '#fde68a',
                      borderRadius: 2,
                      p: 3,
                      bgcolor: '#fffbeb',
                      textAlign: 'center',
                    }}
                  >
                    <HelpOutline sx={{ color: '#b45309', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: '#92400e', fontWeight: 500 }}>
                      No matching purchase found
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Searched up to {creditReturns?.positive_days ?? 90} days before this refund
                    </Typography>
                  </Box>
                )}
              </DialogContent>
            </>
          );
        })()}
      </Dialog>

      {/* Transaction Detail Dialog */}
      <Dialog
        open={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        {selectedTransaction && (() => {
          const t = selectedTransaction;
          const isSpending = t.amount > 0;
          const accentColor = isSpending ? '#ef4444' : '#10b981';
          return (
            <>
              <DialogTitle
                sx={{
                  background: `linear-gradient(135deg, ${accentColor}15 0%, ${accentColor}08 100%)`,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 2,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                    {t.logo_url ? (
                      <Avatar src={t.logo_url} alt={t.merchant_name || ''} imgProps={{ loading: 'lazy', decoding: 'async' }} sx={{ width: 48, height: 48 }} variant="rounded" />
                    ) : (
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: accentColor,
                          fontSize: '1.1rem',
                          fontWeight: 700,
                        }}
                        variant="rounded"
                      >
                        {(t.merchant_name || t.name || '?').charAt(0).toUpperCase()}
                      </Avatar>
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" fontWeight={700} noWrap>
                        {t.merchant_name || t.name}
                      </Typography>
                      {t.merchant_name && t.merchant_name !== t.name && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {t.name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <IconButton onClick={() => setSelectedTransaction(null)} size="small">
                    <Close />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ pt: 3 }}>
                {/* Amount */}
                <Box sx={{ textAlign: 'center', py: 2, mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                    {isSpending ? 'Amount Spent' : 'Amount Received'}
                  </Typography>
                  <Typography
                    variant="h3"
                    fontWeight={800}
                    sx={{ color: accentColor, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {isSpending ? '-' : '+'}{formatCurrency(Math.abs(t.amount))}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t.name}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Details Grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                      Date
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                      {formatShortDate(t.date)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                      Type
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                      {isSpending ? 'Expense' : 'Income'}
                    </Typography>
                  </Box>
                  {t.merchant_name && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                        Merchant
                      </Typography>
                      <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                        {t.merchant_name}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Category */}
                {(t.personal_finance_category || (t.category && t.category.length > 0)) && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, display: 'block', mb: 1 }}>
                      Category
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {t.personal_finance_category ? (
                        <>
                          <Chip label={t.personal_finance_category.primary} size="small" color="primary" />
                          {t.personal_finance_category.detailed && t.personal_finance_category.detailed !== t.personal_finance_category.primary && (
                            <Chip label={t.personal_finance_category.detailed} size="small" variant="outlined" />
                          )}
                        </>
                      ) : (
                        t.category?.slice(0, 3).map((cat, i) => (
                          <Chip key={i} label={cat} size="small" variant="outlined" />
                        ))
                      )}
                    </Box>
                  </Box>
                )}

                {/* Transaction ID */}
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
                    Transaction ID: {t.transaction_id}
                  </Typography>
                </Box>
              </DialogContent>
            </>
          );
        })()}
      </Dialog>

      {/* Sync Message Snackbar */}
      <Snackbar
        open={showSyncMessage}
        autoHideDuration={6000}
        onClose={() => setShowSyncMessage(false)}
        message={syncMessage}
      />
    </Container>
  );
};

export default Dashboard;
